import type { ApplicationDraft, AssessmentResult } from "../types/assessment";
import { publicConfig } from "../config/public";
export interface SessionCredentials {
  sessionId: string;
  accessToken: string;
}
export interface AssessmentSnapshot {
  application?: ApplicationDraft;
  result?: AssessmentResult;
  uploads?: Array<{
    id: string;
    evidenceType: string;
    fileName: string;
    mimeType: string;
    storagePath: string;
  }>;
  documents?: Array<{ id: string; documentType: string; fileName: string }>;
}
export interface RejectionAnalysis {
  code: string;
  category: string;
  eligibility: "resubmittable" | "not_resubmittable" | "review_required";
  summary: string;
  correctiveActions: string[];
  confidence: number;
  sources: Array<{ title: string; url: string }>;
}
const mockMode =
  import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" ||
  (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "false");
const apiBaseUrl = publicConfig.apiBaseUrl.replace(/\/$/, "");
const apiUrl = (path: string) =>
  apiBaseUrl
    ? `${apiBaseUrl}${path}`
    : `/gateway${path.replace(/^\/api(?=\/|$)/, "")}`;
function isCredentials(value: unknown): value is SessionCredentials {
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof Reflect.get(value, "sessionId") === "string" &&
    typeof Reflect.get(value, "accessToken") === "string",
  );
}
function isSnapshot(value: unknown): value is AssessmentSnapshot {
  return Boolean(
    value &&
    typeof value === "object" &&
    (Reflect.get(value, "application") === undefined ||
      typeof Reflect.get(value, "application") === "object") &&
    (Reflect.get(value, "result") === undefined ||
      typeof Reflect.get(value, "result") === "object"),
  );
}
function isSaveResult(value: unknown): value is { applicationId: string } {
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof Reflect.get(value, "applicationId") === "string",
  );
}
function isUploadTicket(
  value: unknown,
): value is { path: string; token: string } {
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof Reflect.get(value, "path") === "string" &&
    typeof Reflect.get(value, "token") === "string",
  );
}
function isSignedDownload(value: unknown): value is { signedUrl: string } {
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof Reflect.get(value, "signedUrl") === "string",
  );
}
function isRejectionAnalysis(value: unknown): value is RejectionAnalysis {
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof Reflect.get(value, "code") === "string" &&
    typeof Reflect.get(value, "summary") === "string" &&
    Array.isArray(Reflect.get(value, "correctiveActions")) &&
    Array.isArray(Reflect.get(value, "sources")),
  );
}
const authHeaders = (c: SessionCredentials) => ({
  authorization: `Bearer ${c.accessToken}`,
  "x-assessment-session-id": c.sessionId,
});
async function parseError(response: Response) {
  const value: unknown = await response.json().catch(() => null);
  const message =
    value && typeof value === "object" ? Reflect.get(value, "error") : null;
  return typeof message === "string" ? message : "Request failed.";
}
function secureToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

export async function createAssessmentSession(): Promise<SessionCredentials> {
  if (mockMode)
    return { sessionId: crypto.randomUUID(), accessToken: secureToken() };
  const response = await fetch(apiUrl("/api/create-session"), {
    method: "POST",
  });
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isCredentials(value)) throw new Error("Invalid session response.");
  return value;
}
export async function saveAnonymousApplication(
  credentials: SessionCredentials,
  draft: ApplicationDraft,
) {
  if (mockMode) return { applicationId: draft.id };
  const response = await fetch(apiUrl("/api/save-application"), {
    method: "POST",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify({ application: draft }),
  });
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isSaveResult(value)) throw new Error("Invalid application save response.");
  return value;
}
export async function getAssessmentSnapshot(
  credentials: SessionCredentials,
): Promise<AssessmentSnapshot> {
  if (mockMode) return {};
  const response = await fetch(apiUrl("/api/get-assessment"), {
    headers: authHeaders(credentials),
  });
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isSnapshot(value)) throw new Error("Invalid assessment response.");
  return value;
}
export async function createUploadUrl(
  credentials: SessionCredentials,
  input: {
    applicationId: string;
    evidenceType: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
  },
) {
  const response = await fetch(apiUrl("/api/create-upload-url"), {
    method: "POST",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isUploadTicket(value)) throw new Error("Invalid upload response.");
  return value;
}
export async function confirmUpload(
  credentials: SessionCredentials,
  path: string,
) {
  const response = await fetch(apiUrl("/api/confirm-upload"), {
    method: "POST",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify({ path }),
  });
  if (!response.ok) throw new Error(await parseError(response));
}
export async function deleteUpload(
  credentials: SessionCredentials,
  path: string,
) {
  const response = await fetch(apiUrl("/api/delete-upload"), {
    method: "DELETE",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify({ path }),
  });
  if (!response.ok) throw new Error(await parseError(response));
}
export async function getDownloadUrl(
  credentials: SessionCredentials,
  documentId: string,
) {
  const response = await fetch(
    apiUrl(
      `/api/get-download-url?documentId=${encodeURIComponent(documentId)}`,
    ),
    { headers: authHeaders(credentials) },
  );
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isSignedDownload(value)) throw new Error("Invalid download response.");
  return value;
}
export async function startAnonymousAssessment(
  credentials: SessionCredentials,
  applicationId: string,
  turnstileToken: string,
) {
  if (mockMode) return;
  const response = await fetch(apiUrl("/api/start-assessment"), {
    method: "POST",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify({ applicationId, turnstileToken }),
  });
  if (!response.ok) throw new Error(await parseError(response));
}
export async function deleteAssessmentSession(credentials: SessionCredentials) {
  if (mockMode) return;
  const response = await fetch(apiUrl("/api/delete-assessment"), {
    method: "DELETE",
    headers: authHeaders(credentials),
  });
  if (!response.ok) throw new Error(await parseError(response));
}

const REJECTION_GUIDE =
  "https://help.gohighlevel.com/support/solutions/articles/155000007572-understanding-a2p-campaign-rejection-reasons-required-fixes";

export async function analyzeRejectionCode(
  credentials: SessionCredentials,
  input: {
    code: string;
    businessName: string;
    usesDba: boolean;
    rejectionDetails: string;
    turnstileToken: string;
  },
): Promise<RejectionAnalysis> {
  if (mockMode) {
    const known: Record<string, Omit<RejectionAnalysis, "code">> = {
      "30909": {
        category: "Call-to-action / opt-in",
        eligibility: "resubmittable",
        summary:
          "The campaign's opt-in path or call-to-action did not provide enough verifiable consent detail for review.",
        correctiveActions: [
          "Show the exact legal business name—or DBA, if one is used—beside the phone field and consent statement.",
          "Make message frequency, message and data rates, STOP/HELP, Terms, and Privacy links visible before submission.",
          "Capture a current screenshot and update the campaign description to match the live opt-in flow.",
        ],
        confidence: 0.92,
        sources: [
          { title: "GoHighLevel rejection code guide", url: REJECTION_GUIDE },
        ],
      },
      "30882": {
        category: "Website / brand identity",
        eligibility: "resubmittable",
        summary:
          "The reviewer could not verify a clear relationship between the legal business and the customer-facing DBA.",
        correctiveActions: [
          "Add the legal business–DBA relationship to a persistent website header or footer.",
          "Use the same DBA in opt-in language and all sample messages.",
          "Confirm that the submitted website is public, complete, and uses HTTPS before resubmitting.",
        ],
        confidence: 0.89,
        sources: [
          { title: "GoHighLevel rejection code guide", url: REJECTION_GUIDE },
        ],
      },
    };
    return {
      code: input.code,
      ...(known[input.code] ?? {
        category: "Manual documentation review",
        eligibility: "review_required" as const,
        summary:
          "This code needs to be matched against the current provider guidance before a resubmission decision is made.",
        correctiveActions: [
          "Confirm the complete five-digit rejection code in the Trust Center.",
          "Compare the code with the current GoHighLevel required-fixes table.",
          "Correct only the cited issue, then verify business-name consistency across the submission.",
        ],
        confidence: 0.62,
        sources: [
          { title: "GoHighLevel rejection code guide", url: REJECTION_GUIDE },
        ],
      }),
    };
  }
  const response = await fetch(apiUrl("/api/analyze-rejection"), {
    method: "POST",
    headers: {
      ...authHeaders(credentials),
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await parseError(response));
  const value: unknown = await response.json();
  if (!isRejectionAnalysis(value))
    throw new Error("Invalid rejection analysis response.");
  return value;
}
